import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import {
  classifyDshbClassKind,
  DSHB_CLASS_KINDS,
  isDshbGridKind,
  isDshbPlaceOnlyGridKind,
  isDshbScoredGridKind,
  resolveConfirmedDshbClassKind,
  buildDshbScoredGridColumns,
} from "score-common";
import AppShell from "./AppShell";
import PageBreadcrumbs from "./PageBreadcrumbs";
import SplitClassChip from "./SplitClassChip";
import ClassGridRow from "./ClassGridRow";
import ClassCeremony from "./ClassCeremony";
import { useRidesForClass } from "../hooks/useRidesForClass";
import { useClassGridTest } from "../hooks/useClassGridTest";
import { classNumberLabel } from "../util/classDisplay";
import { sortByCompetitorNumber } from "../util/classGridNav";

// Group test.observations by their `group` label (e.g. "Walk"/"Trot"/
// "Canter") preserving first-seen order, so the header can span each group
// over its sub-columns. #1747 — observations are deliberately NOT sections:
// no coefficient, never summed. Optionally drops any group named in
// `hiddenGroups` — see the #1750 BLOCKER 2 comment on ClassGrid below for
// why that list exists and where it is allowed to come from.
function groupObservations(observations, hiddenGroups) {
  const groups = [];
  const byGroup = new Map();
  for (const obs of observations ?? []) {
    if (hiddenGroups.includes(obs.group)) continue;
    if (!byGroup.has(obs.group)) {
      const g = { group: obs.group, cols: [] };
      byGroup.set(obs.group, g);
      groups.push(g);
    }
    byGroup.get(obs.group).cols.push(obs);
  }
  return groups;
}

// #1750 BLOCKER 2 (round-2 judge review) — DR133.1.a permits a class limited
// to medium walk and working trot, and DR133.1.k requires that class to
// still use the same DSE scoresheet, so "this class is walk/trot only" is a
// property of the CLASS, not the test def, and it cannot become a def field
// (one published sheet = one def, #1440 SS0a). #1749's two DSE defs both
// carry all six observations, canter included, so the def alone never hides
// anything; something has to compute `hiddenGroups` below.
//
// RESOLVED (card #1783, Jaybill 2026-08-30): the signal is a human-set,
// class-level flag authored in scoreadmin-fe (Class.walkTrotOnly,
// score-common's classSchema). Inferring it from the FVD class name was
// ruled out by a prod check (19/19 equitation classes carry no walk/trot
// marker in their name); a scribe-side toggle was rejected as device-local
// and invisible to other consumers.
//
// `hiddenGroups` is the ONE place that signal is allowed to enter this
// component: an array of `test.observations[].group` values to exclude from
// both the header and every row's cells, defaulting to [] so the grid shows
// every group the resolved test carries. Do not add a second entry point
// (no special-casing "Canter" by name anywhere else in this file or in
// ClassGridRow.jsx): the class's own `walkTrotOnly` flag below is the only
// thing that computes a non-default value.
// Module-level so both arrays are the SAME identity on every render. A
// `= []`/`= [...]` default/literal allocates a fresh array each render,
// which changes the identity `observationGroups`' useMemo is keyed on, so
// the memo never hit and every row's `observations` prop was a new array
// too (round-6 judge review).
const NO_HIDDEN_GROUPS = [];
// #2184 — same module-level stable-identity discipline as NO_HIDDEN_GROUPS
// above, for scoredColumns' own useMemo (and every row's).
const EMPTY_SCORED_COLUMNS = [];
// The only group DR133.1.a's walk/trot-only classes drop; see
// docs/score/2023_tests/usef-dressage-seat-equitation.json's own
// observations for the "Canter" group label this matches verbatim.
const WALK_TROT_ONLY_HIDDEN_GROUPS = ["Canter"];

export default function ClassGrid({ hiddenGroups = NO_HIDDEN_GROUPS }) {
  const { classId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation("score");
  const { data, settled } = useRidesForClass(classId);
  const klass = data?.klass;
  const rides = data?.rides ?? [];
  const { test, loading: testLoading } = useClassGridTest(klass, rides);
  // #2678 PATH 1 / #2821 — `!settled` replaces the old `loading` gate below:
  // `loading` (useCachedResource, common-fe) never resets to `true` on a
  // classId change (#2162), so it could read `false` while `klass`/`rides`
  // still held a PREVIOUS classId's settled answer. `settled` resets to
  // `false` on a genuine deps change (unless the new classId already has a
  // warm-cache hit, itself a real synchronous settle), so gating on it here
  // holds the grid behind its loading state until the CURRENT classId's own
  // answer lands instead of briefly rendering the wrong class.
  const ridesUnsettled = !settled;

  // #2183 — resolved fresh off the CLASS's className, never off the reused
  // Test row's own sheetLayout/scoringModel (see dshb-class-kind.js's doc
  // comment: FVD stamps a championship/suitability class with its
  // QUALIFYING class's Test id, so `test` here is the SAME shared row an
  // ordinary qualifying-class ride uses).
  // #2277 — the stored per-class override wins over the className heuristic.
  const rawDshbKind = classifyDshbClassKind({
    className: klass?.className,
    fvdTestId: test?.fvdId,
    dshbKindOverride: klass?.dshbClassKindOverride,
  });
  // #2745 - same shared gate RideList.jsx uses before routing here in the
  // first place: an unconfirmed plain CHAMPIONSHIP (className match, no DSHB
  // signal) must not render the place-only layout either, or a direct/stale
  // link to /grid would still show it (see resolveConfirmedDshbClassKind's
  // own doc comment).
  const dshbKind = resolveConfirmedDshbClassKind({
    kind: rawDshbKind,
    fvdTestId: test?.fvdId,
    test,
    dshbKindOverride: klass?.dshbClassKindOverride,
  });
  // #2184 — the two grid families (score-common's dshb-class-kind.js is the
  // single shared root; see that file's own comment for why these must not
  // be re-derived inline per component): place-only (CHAMPIONSHIP/
  // SUITABILITY, no Total column, no scored cells) vs. scored (MATERIALE/
  // UNDER_SADDLE, DR211.4 movement-totals — real coefficient-weighted marks
  // and a computed Total). isDshbGridClass is their union, used ONLY for
  // "stay on the grid regardless of sheetLayout" below; anywhere that needs
  // to tell the two families apart uses the two narrower flags.
  const isDshbPlaceOnlyGridClass = isDshbPlaceOnlyGridKind(dshbKind);
  const isDshbScoredGridClass = isDshbScoredGridKind(dshbKind);
  const isDshbGridClass = isDshbGridKind(dshbKind);

  // Defensive: a scribe navigating straight to /grid on a movement-grid (or
  // still-unresolved) class belongs on the ordinary ride list, not here.
  // RideList's own redirect (the normal path in) never produces this case.
  // #2183 — a CHAMPIONSHIP/SUITABILITY class stays on the grid regardless of
  // test.sheetLayout (see above): the reused Test's own layout is the
  // qualifying class's, "movement-grid" on the real WEC/Del Mar files.
  // #2184 — MATERIALE/UNDER_SADDLE stay for the analogous reason: their own
  // real Test row's sheetLayout is never authored "class-grid" either.
  useEffect(() => {
    if (!testLoading && test && test.sheetLayout !== "class-grid" && !isDshbGridClass) {
      navigate(`/classes/${classId}`, { replace: true });
    }
  }, [testLoading, test, isDshbGridClass, classId, navigate]);

  // #1783: the computed value #1750 BLOCKER 2 left open: a walk/trot-only
  // class always hides Canter, IN ADDITION to whatever (if anything) a
  // caller passed as `hiddenGroups` — this is a union, never a replacement,
  // so a future caller-supplied value is never silently discarded. useMemo
  // is required here, not optional: a fresh array literal on every render
  // would defeat the module-level identity discipline above and re-trigger
  // `observationGroups`' own memo (and every row's) on every render, the
  // exact bug the round-6 judge comment on `NO_HIDDEN_GROUPS` documents.
  const effectiveHiddenGroups = useMemo(
    () =>
      klass?.walkTrotOnly
        ? [...new Set([...WALK_TROT_ONLY_HIDDEN_GROUPS, ...hiddenGroups])]
        : hiddenGroups,
    [klass?.walkTrotOnly, hiddenGroups],
  );

  const observationGroups = useMemo(
    () => groupObservations(test?.observations, effectiveHiddenGroups),
    [test?.observations, effectiveHiddenGroups],
  );
  const flatObservations = useMemo(
    () => observationGroups.flatMap((g) => g.cols),
    [observationGroups],
  );

  // #2184 — the scored columns for a MATERIALE/UNDER_SADDLE row: Walk/Trot/
  // Canter/General Impression (or whatever the resolved test actually
  // carries — see buildDshbScoredGridColumns's own comment), each a REAL
  // mark with a coefficient, unlike observationGroups above (unscored, no
  // coefficient, DSE/DSM only). Empty for every other class, including
  // place-only — nothing to score there either.
  const scoredColumns = useMemo(
    () => (isDshbScoredGridClass ? buildDshbScoredGridColumns(test) : EMPTY_SCORED_COLUMNS),
    [isDshbScoredGridClass, test],
  );

  const sortedRides = useMemo(() => sortByCompetitorNumber(rides), [rides]);

  // F4 (round-2 judge review) — the missing-Total count used to read
  // Ride.percentage (the reconcile-stamped cache from useRidesForClass), not
  // the live sheet, so it never decremented as the scribe typed and read
  // "N of N still need a Total" forever on a fresh class. Each row reports
  // its own live sheet.percentage state up via onTotalChange; a row that
  // hasn't reported yet (still loading) counts as "still needs a Total" —
  // the same conservative default the old cache-based count effectively had
  // before any row had scored anything.
  const [hasTotalByRide, setHasTotalByRide] = useState({});
  const onTotalChange = useCallback((rideId, hasTotal) => {
    setHasTotalByRide((prev) => (prev[rideId] === hasTotal ? prev : { ...prev, [rideId]: hasTotal }));
  }, []);
  // #2183 — DR133.1.j (the rule this gate enforces) does not apply to a
  // place-only class: there is no Total column, no percentage to be
  // missing, so the count (and the banner/confirm-gate it drives) is forced
  // to 0 rather than reading hasTotalByRide, which would otherwise stay
  // permanently "missing" (typeof sheet?.percentage === "number" is
  // permanently false on a placement-only sheet) and block confirm forever.
  // #2184 — a SCORED grid class (MATERIALE/UNDER_SADDLE) DOES have a real
  // Total (DR211.4/DR211.5: a computed percentage against a possible 100),
  // so it is NOT forced to 0 here — it counts the same as an ordinary/
  // observations-grid class, just against a different "hasTotal" signal
  // (ClassGridRow reports whether every scored cell on the row is filled,
  // since a scored sheet's percentage is server-computed only at confirm/
  // complete time, not typed by the scribe).
  const missingTotalCount = isDshbPlaceOnlyGridClass
    ? 0
    : sortedRides.filter((r) => hasTotalByRide[r.id] !== true).length;

  // ── #1751: the class-level ceremony ───────────────────────────────────
  //
  // Each row owns its own ScoreSheet (never lift that to the parent, see
  // ClassGridRow's header), so a class-level action is issued as a COMMAND the
  // rows execute against their own sheets. `ceremonyOp` is that command;
  // `rowStates` is what comes back the other way.
  const [rowStates, setRowStates] = useState({});
  const onSheetStateChange = useCallback((rideId, next) => {
    setRowStates((prev) => {
      const cur = prev[rideId];
      if (
        cur &&
        cur.isConfirmed === next.isConfirmed &&
        cur.isCompleted === next.isCompleted &&
        cur.signedKey === next.signedKey &&
        cur.noSignatureNeeded === next.noSignatureNeeded
      ) {
        return prev;
      }
      return { ...prev, [rideId]: next };
    });
  }, []);

  // Shape the ceremony reads: signedKey (a string, so it can be an effect dep
  // in the row) split back into the ids it encodes.
  const ceremonyRowStates = useMemo(() => {
    const out = {};
    for (const [rideId, st] of Object.entries(rowStates)) {
      out[rideId] = {
        isConfirmed: st.isConfirmed,
        isCompleted: st.isCompleted,
        signedJudgeIds: st.signedKey ? st.signedKey.split(",") : [],
        noSignatureNeeded: st.noSignatureNeeded,
      };
    }
    return out;
  }, [rowStates]);

  const [ceremonyOp, setCeremonyOp] = useState(null);
  const [ceremonyErrorCount, setCeremonyErrorCount] = useState(0);

  // Rows report per ride id, and a row can report twice if it remounts
  // mid-fan-out, so progress is counted over a SET of ride ids rather than a
  // running total — a double report would otherwise end the ceremony early,
  // while rows were still working, and that is precisely the half-finished
  // state this card exists to make impossible to reach by accident.
  //
  // These are refs, not state, for two reasons. onCeremonyResult must be
  // IDENTITY-STABLE (every row holds it in a useEffect dependency list, so a
  // new identity would re-fire the command in every row), and the bookkeeping
  // must not live inside a setState updater — React may invoke an updater
  // twice, which would double-count errors.
  const reportedRef = useRef(new Set());
  const errorCountRef = useRef(0);
  // ceremonyOpRef is the SOURCE OF TRUTH for the in-flight op, and it is
  // deliberately never assigned from render. Mirroring `ceremonyOp` into it on
  // every render looked harmless and was not: runCeremony sets the ref
  // imperatively and then calls setState, so any render that happened in
  // between (a row reporting its own state is enough, and 25 of them do that
  // on every ceremony) wrote the STALE state value back over the fresh op.
  // Rows then reported against an op the ref no longer recognised, their
  // reports were dropped, and the ceremony never ended: the busy dialog stayed
  // up and the next stage's buttons stayed disabled forever. Caught by the
  // AC4 Cypress test, which is the only one that runs two stages in a row.
  const ceremonyOpRef = useRef(null);
  const rideCountRef = useRef(0);
  rideCountRef.current = sortedRides.length;

  const runCeremony = useCallback((kind, judgeId) => {
    reportedRef.current = new Set();
    errorCountRef.current = 0;
    setCeremonyErrorCount(0);
    const op = { opId: `${kind}:${judgeId ?? ""}:${Date.now()}`, kind, judgeId, done: 0 };
    ceremonyOpRef.current = op;
    setCeremonyOp(op);
  }, []);

  const onCeremonyResult = useCallback((rideId, opId, error) => {
    const op = ceremonyOpRef.current;
    if (!op || op.opId !== opId || reportedRef.current.has(rideId)) return;
    reportedRef.current.add(rideId);
    if (error) errorCountRef.current += 1;

    const done = reportedRef.current.size;
    if (done >= rideCountRef.current) {
      ceremonyOpRef.current = null;
      setCeremonyErrorCount(errorCountRef.current);
      setCeremonyOp(null);
    } else {
      const next = { ...op, done };
      ceremonyOpRef.current = next;
      setCeremonyOp(next);
    }
  }, []);

  const rowElRefs = useRef({});
  const firstInputRefs = useRef({});
  const [jumpValue, setJumpValue] = useState("");

  // Round-6 judge review — stable identities (the rows hold these in
  // useCallback deps), and a null means the row unmounted, so DROP the entry
  // rather than leaving a dead node behind for jumpToRider to scroll to. A
  // class whose ride list refreshes mid-session used to accumulate one stale
  // entry per removed ride here, forever.
  const registerRowEl = useCallback((id, el) => {
    if (el) rowElRefs.current[id] = el;
    else delete rowElRefs.current[id];
  }, []);
  const registerFirstInput = useCallback((id, el) => {
    if (el) firstInputRefs.current[id] = el;
    else delete firstInputRefs.current[id];
  }, []);

  const jumpToRider = (e) => {
    if (e.key !== "Enter") return;
    const target = sortedRides.find(
      (r) => (r.competitorNumber ?? "").trim() === jumpValue.trim(),
    );
    if (target) {
      rowElRefs.current[target.id]?.scrollIntoView({ block: "center" });
      firstInputRefs.current[target.id]?.focus();
    }
  };

  // KIOSK DOCTRINE (#1751 AC6). Mid-ceremony there is no route out: the busy
  // dialog is modal with no dismiss, and the one navigational affordance on
  // this screen loses its link so it cannot be tabbed to and followed either.
  // A scribe who wanders off between "confirm" and "the third judge signs"
  // leaves the class in exactly the half-finished state this card is about.
  const crumbs = [
    { label: t("classes.title"), to: ceremonyOp ? undefined : "/classes" },
    { label: klass ? classNumberLabel(klass) : "…" },
  ];

  return (
    <AppShell sessionSubtitle={klass ? classNumberLabel(klass) : undefined} classId={classId}>
      <PageBreadcrumbs items={crumbs} />

      {(ridesUnsettled || testLoading) && (
        <Box sx={{ mt: 2 }}>
          <Typography color="text.secondary">{t("classGrid.loading")}</Typography>
        </Box>
      )}

      {!ridesUnsettled && !testLoading && klass && (
        <Box sx={{ mb: 2 }}>
          {/* #1761 — same "Split/RO" chip the class list/search carry (#883),
              so the grid for a split section is never mistaken for its base
              class's. */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography variant="h5" fontWeight={700}>
              {classNumberLabel(klass)}
            </Typography>
            <SplitClassChip klass={klass} testId="class-grid-split-chip" />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t("classGrid.ridersSummary", { count: sortedRides.length })}
          </Typography>

          <TextField
            size="small"
            label={t("classGrid.jumpToRider")}
            sx={{ mt: 1.5, maxWidth: 200 }}
            value={jumpValue}
            onChange={(e) => setJumpValue(e.target.value)}
            onKeyDown={jumpToRider}
            inputProps={{ "data-testid": "class-grid-jump-input" }}
          />

          {missingTotalCount > 0 && (
            <Alert severity="warning" sx={{ mt: 1.5 }} data-testid="class-grid-missing-total-banner">
              {/* #2184 — a scored grid class (DR211.4/DR211.5) cites its own
                  rule family, never DR133.1.j (a DSE/DSM-only citation).
                  #2235 — DR211.5 (docs/rules/usef/rulebook-dr-dressage-2026.pdf,
                  labelled page DR-74, not DR-73: the "(c) USEF 2026 / DR-73"
                  footer falls between DR211.1 and DR211.2, not before .5)
                  has TWO separate clauses: scores are reported as a total
                  against a possible 100%, and SEPARATELY "all scores (if
                  given) and placings must be published". Do not weld them
                  into "placings published as a percentage" - placings are
                  not percentages. The banner must not claim a score is
                  required for every rider either. */}
              {t(
                isDshbScoredGridClass
                  ? "classGrid.missingTotalBannerDshb"
                  : "classGrid.missingTotalBanner",
                {
                  count: missingTotalCount,
                  total: sortedRides.length,
                },
              )}
            </Alert>
          )}
        </Box>
      )}

      {!ridesUnsettled && !testLoading && sortedRides.length > 0 && (
        <TableContainer
          sx={{
            maxHeight: "70vh",
            overflow: "auto",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <Table size="small" stickyHeader data-testid="class-grid-table">
            <TableHead>
              <TableRow>
                <TableCell
                  rowSpan={2}
                  sx={{
                    position: "sticky",
                    left: 0,
                    zIndex: 3,
                    bgcolor: "background.paper",
                    borderRight: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  {t("classGrid.riderNoColumn")}
                </TableCell>
                {/* #2184 — a scored grid class (MATERIALE/UNDER_SADDLE) has no
                    observationGroups at all (test.observations stays empty for
                    these defs — they score real movements/collectives
                    instead), so it renders scoredColumns here in exactly the
                    same header slot, one cell per scored column (coefficient
                    goes in the sub-header row below, mirroring how a DSE
                    group's sub-columns work). */}
                {isDshbScoredGridClass
                  ? scoredColumns.map((col) => (
                      <TableCell
                        key={`${col.type}-${col.movementNumber}`}
                        align="center"
                        sx={{ bgcolor: "background.paper" }}
                      >
                        {col.label}
                      </TableCell>
                    ))
                  : observationGroups.map((g) => (
                      <TableCell
                        key={g.group}
                        align="center"
                        colSpan={g.cols.length}
                        sx={{ bgcolor: "background.paper" }}
                      >
                        {g.group}
                      </TableCell>
                    ))}
                <TableCell rowSpan={2} sx={{ bgcolor: "background.paper" }}>
                  {t("classGrid.notesColumn")}
                </TableCell>
                {/* #2183 — no Total column at all for a place-only class: DR204.2.c/
                    DR210.5 award no scores, so there is nothing to type into and
                    nothing to autosave (not merely hidden — see ClassGridRow).
                    #2184 — a SCORED grid class DOES get a Total column (a real,
                    computed percentage), so it is gated on isDshbPlaceOnlyGridClass
                    now, not the old union. */}
                {!isDshbPlaceOnlyGridClass && (
                  <TableCell
                    rowSpan={2}
                    align="center"
                    sx={{ bgcolor: "background.paper", fontWeight: 700 }}
                  >
                    {t("classGrid.totalColumn")}
                  </TableCell>
                )}
                {/* #2183 — Championship Class Sheet's "Prior Score" column
                    (DR210.5). CHAMPIONSHIP only: Suitability (DR204.2.c) is a
                    first-round class, not a re-judging, and has no such column. */}
                {dshbKind === DSHB_CLASS_KINDS.CHAMPIONSHIP && (
                  <TableCell
                    rowSpan={2}
                    align="center"
                    sx={{ bgcolor: "background.paper", fontWeight: 700 }}
                  >
                    {t("classGrid.priorScoreColumn")}
                  </TableCell>
                )}
                <TableCell
                  rowSpan={2}
                  align="center"
                  sx={{ bgcolor: "background.paper", fontWeight: 700 }}
                >
                  {t("classGrid.placeColumn")}
                </TableCell>
              </TableRow>
              <TableRow>
                {/* #2184 — the scored columns' sub-header shows the
                    coefficient (e.g. "×3"), the printed sheet's own
                    "Coefficient x3" cell — AC2's "coefficients visible". */}
                {isDshbScoredGridClass
                  ? scoredColumns.map((col) => (
                      <TableCell
                        key={`${col.type}-${col.movementNumber}-coef`}
                        align="center"
                        sx={{ bgcolor: "background.paper" }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          {`×${col.coefficient}`}
                        </Typography>
                      </TableCell>
                    ))
                  : flatObservations.map((obs) => (
                      <TableCell
                        key={obs.key}
                        align="center"
                        sx={{ bgcolor: "background.paper" }}
                      >
                        {obs.label}
                      </TableCell>
                    ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRides.map((ride) => (
                <ClassGridRow
                  key={ride.id}
                  ride={ride}
                  klass={klass}
                  test={test}
                  dshbKind={dshbKind}
                  observations={flatObservations}
                  scoredColumns={scoredColumns}
                  registerRowEl={registerRowEl}
                  registerFirstInput={registerFirstInput}
                  onTotalChange={onTotalChange}
                  onSheetStateChange={onSheetStateChange}
                  ceremonyOp={ceremonyOp}
                  onCeremonyResult={onCeremonyResult}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!ridesUnsettled && !testLoading && sortedRides.length === 0 && klass && (
        <Typography color="text.secondary">{t("classGrid.noRides")}</Typography>
      )}

      {!ridesUnsettled && !testLoading && klass && sortedRides.length > 0 && (
        <>
          {ceremonyErrorCount > 0 && (
            <Alert severity="error" sx={{ mt: 2 }} data-testid="ceremony-errors">
              {t("classCeremony.errorTitle")}{" "}
              {t("classCeremony.errorBody", {
                count: ceremonyErrorCount,
                total: sortedRides.length,
              })}
            </Alert>
          )}
          <ClassCeremony
            klass={klass}
            test={test}
            rides={sortedRides}
            missingTotalCount={missingTotalCount}
            isScoredGrid={isDshbScoredGridClass}
            rowStates={ceremonyRowStates}
            busyOp={ceremonyOp}
            onRunCeremony={runCeremony}
          />
        </>
      )}
    </AppShell>
  );
}
